/**
 * Session detail tab (participants).
 */
import * as React from 'react';
import { Users, Video, CheckCircle2, XCircle } from 'lucide-react';
import { DetailsCard } from '@/components/common/DetailsCard';
import { ParticipantChip } from '@/components/common/ParticipantChip';
import { EmptyPlaceholder } from '@/components/common/details/EmptyPlaceholder';
import { LabelBadge } from '@/components/common/LabelBadge';
import {
  LIVEKIT_ROOM_STATUS_CONFIG,
  LIVEKIT_BUSINESS_ROLE_ANIMATOR_CONFIG,
  SESSION_PARTICIPANT_ROLE_LABELS,
} from '@/components/sessions/session-configs';
import { resolveLucideIcon } from '@/hooks/useRoleConfig';
import type { SessionWithParticipants } from '@/types/inperson/session.types';
import type { AttendanceStatus } from '@/types/attendance.types';
import type {
  LiveKitRoomPresenceStatus,
  SessionLiveKitPresenceResponse,
} from '@/types/remote/session.types';

/** Props for {@link SessionParticipantsTab}. */
interface SessionParticipantsTabProps {
  session: SessionWithParticipants;
  presenceResponse: SessionLiveKitPresenceResponse | undefined;
  presenceLoading: boolean;
}

/** Session detail tab (participants). */
export const SessionParticipantsTab: React.FC<SessionParticipantsTabProps> = ({
  session,
  presenceResponse,
  presenceLoading,
}) => {
  /** Tutors and volunteers shown as the tutor-participants list. */
  const tutorParticipants =
    session.participants?.filter((p) => p.role === 'volunteer' || p.role === 'tutor') || [];
  const hasParticipants = tutorParticipants.length > 0;

  return (
    <div className="details-view-grid">
      <DetailsCard icon={Users} title="Participants Tuteurs" colorName="color-3" colSpan="full">
        {hasParticipants ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-[var(--color-3-nuance-1)]" />
              <span className="text-sm font-semibold text-[var(--color-3-nuance-1)]">
                {tutorParticipants.length}
                {session.maxStudents ? ` / ${session.maxStudents}` : ''} participant
                {tutorParticipants.length > 1 ? 's' : ''}
              </span>
            </div>
            <div className="participant-chip-list">
              {tutorParticipants.map((participant) => (
                <ParticipantChip
                  key={participant.id}
                  user={participant.user}
                  role={participant.role as 'tutor' | 'volunteer'}
                  size="md"
                  showTooltip
                  attendanceStatus={(participant.attendanceStatus as AttendanceStatus) || 'pending'}
                  absenceJustification={participant.absenceJustification}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {Boolean(session.maxStudents) && (
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-[var(--color-3-nuance-1)]" />
                <span className="text-sm font-semibold text-[var(--color-3-nuance-1)]">
                  0 / {session.maxStudents} participants
                </span>
              </div>
            )}
            <EmptyPlaceholder message="Aucun participant pour le moment" />
          </div>
        )}
      </DetailsCard>

      <DetailsCard
        icon={Video}
        title="Présence en temps réel (visio)"
        colorName="color-1"
        colSpan="full"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {presenceLoading ? 'Mise à jour...' : 'Connexions des animateurs à distance'}
            </span>
            {presenceResponse?.roomStatus &&
              (() => {
                const roomCfg =
                  LIVEKIT_ROOM_STATUS_CONFIG[
                    presenceResponse.roomStatus as LiveKitRoomPresenceStatus
                  ];
                if (!roomCfg) return null;
                return (
                  <LabelBadge
                    label={roomCfg.label}
                    icon={roomCfg.icon}
                    variant={roomCfg.badgeVariant}
                    color={roomCfg.badgeColor}
                    size="sm"
                  />
                );
              })()}
          </div>

          {presenceResponse?.participants && presenceResponse.participants.length > 0 ? (
            <div className="space-y-3">
              {presenceResponse.participants.map((presence) => (
                <div key={presence.userId} className="p-3 border rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0"
                        style={{ backgroundColor: 'var(--color-1-nuance-1)' }}
                      >
                        {presence.displayName?.[0] ?? 'P'}
                      </div>
                      <div>
                        <div className="font-medium text-sm">{presence.displayName}</div>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          {(() => {
                            const label =
                              presence.roleLabel ??
                              SESSION_PARTICIPANT_ROLE_LABELS[presence.role] ??
                              presence.role;
                            let color = 'var(--color-1-nuance-2)';
                            if (presence.roleColor?.startsWith('color-')) {
                              color = `var(--${presence.roleColor})`;
                            } else if (presence.roleColor) {
                              color = presence.roleColor;
                            }
                            return (
                              <LabelBadge
                                label={label}
                                icon={
                                  presence.roleIcon
                                    ? resolveLucideIcon(presence.roleIcon)
                                    : undefined
                                }
                                variant="ghost"
                                color={color}
                                size="sm"
                              />
                            );
                          })()}
                          {presence.businessRole === 'animator' && (
                            <LabelBadge
                              label={LIVEKIT_BUSINESS_ROLE_ANIMATOR_CONFIG.label}
                              icon={LIVEKIT_BUSINESS_ROLE_ANIMATOR_CONFIG.icon}
                              variant={LIVEKIT_BUSINESS_ROLE_ANIMATOR_CONFIG.badgeVariant}
                              color={LIVEKIT_BUSINESS_ROLE_ANIMATOR_CONFIG.badgeColor}
                              size="sm"
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    <LabelBadge
                      label={presence.isPresent ? 'Présent' : 'Absent'}
                      icon={presence.isPresent ? CheckCircle2 : XCircle}
                      variant="outlined"
                      color={
                        presence.isPresent
                          ? 'rgb(var(--status-success))'
                          : 'rgb(var(--color-error))'
                      }
                      size="sm"
                    />
                  </div>

                  <dl className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <dt className="text-gray-500">1ère connexion</dt>
                      <dd className="font-medium">
                        {presence.firstJoinedAt
                          ? new Date(presence.firstJoinedAt).toLocaleTimeString('fr-FR', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Dernière déconnexion</dt>
                      <dd className="font-medium">
                        {presence.lastLeftAt
                          ? new Date(presence.lastLeftAt).toLocaleTimeString('fr-FR', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Durée totale</dt>
                      <dd className="font-medium">
                        {presence.totalDurationSeconds
                          ? `${Math.round(presence.totalDurationSeconds / 60)} min`
                          : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Connexions</dt>
                      <dd className="font-medium">{presence.connectionCount ?? 0}</dd>
                    </div>
                  </dl>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              Aucune donnée de présence disponible
              {session.status !== 'IN_PROGRESS' && session.status !== 'COMPLETED' && (
                <span className="block mt-1 text-xs">(La séance n'a pas encore commencé)</span>
              )}
            </p>
          )}
        </div>
      </DetailsCard>
    </div>
  );
};
